import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStytch } from '@stytch/react';
import { useAuthContext } from '@/modules/auth/context/AuthProvider';
import { isStandalone, isWebAuthnAvailable } from '@/modules/auth/utils/isStandalone';
import {
  dismissPasskeyPrompt,
  hasLocalPasskey,
  isPasskeyPromptDismissed,
  setLocalPasskeyEnrolled,
} from '@/modules/auth/utils/passkeyLocal';
import { isPasskeyDismissal } from '@/modules/auth/utils/passkeyError';
import { SESSION_DURATION_MINUTES } from '@/modules/auth/stytchClient';
import { HiOutlineFingerPrint } from 'react-icons/hi';
import { Alert, Button, IconButton } from '@/modules/ui/components';
import * as Sentry from '@sentry/browser';

/**
 * Optional, dismissible passkey nudge — never blocks the app.
 * Shown in the installed PWA after sign-in when WebAuthn is available and unused.
 */
function PasskeyPromptBanner() {
  const { user } = useAuthContext();
  const stytch = useStytch();
  const navigate = useNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) {
      setVisible(false);
      return;
    }
    if (location.pathname === '/account' || location.pathname === '/manage') {
      setVisible(false);
      return;
    }
    const eligible =
      isStandalone() &&
      isWebAuthnAvailable() &&
      !hasLocalPasskey() &&
      !(user.webauthn_registrations?.length > 0) &&
      !isPasskeyPromptDismissed();
    setVisible(eligible);
  }, [user, location.pathname]);

  if (!visible) return null;

  const hide = () => {
    dismissPasskeyPrompt();
    setVisible(false);
  };

  const handleSetup = async () => {
    try {
      setBusy(true);
      setError('');
      await stytch.webauthn.register({
        domain: window.location.hostname,
        authenticator_type: 'platform',
        session_duration_minutes: SESSION_DURATION_MINUTES,
      });
      setLocalPasskeyEnrolled(true, user?.email);
      setVisible(false);
    } catch (err) {
      if (isPasskeyDismissal(err)) {
        setError('');
        return;
      }
      console.error('Passkey prompt registration failed:', err);
      Sentry.captureException(err);
      setError(
        err?.error_message || err?.message || 'Could not set up passkey. You can try again from Account.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-brand-50 dark:bg-brand-900/20 border-b border-brand-200 dark:border-brand-800">
      <div className="max-w-4xl mx-auto px-4 py-3 flex items-start gap-3">
        <div className="mt-0.5 w-9 h-9 rounded-full bg-brand-100 dark:bg-brand-900/40 flex items-center justify-center shrink-0">
          <HiOutlineFingerPrint className="w-5 h-5 text-brand-700 dark:text-brand-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-brand-900 dark:text-brand-100">
            Sign in faster next time?
          </p>
          <p className="text-sm text-brand-800/90 dark:text-brand-200/90 mt-0.5">
            Optional — use Face ID or your fingerprint instead of email. You can also set this up later in Account.
          </p>
          {error && (
            <div className="mt-2">
              <Alert variant="error">{error}</Alert>
            </div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="small"
              onClick={handleSetup}
              disabled={busy}
              loading={busy}
              className="cursor-pointer"
            >
              <HiOutlineFingerPrint className="w-4 h-4" />
              Set up passkey
            </Button>
            <Button
              type="button"
              variant="link"
              size="small"
              onClick={() => {
                hide();
                navigate('/account');
              }}
            >
              Account settings
            </Button>
            <Button
              type="button"
              variant="link"
              size="small"
              onClick={hide}
            >
              Not now
            </Button>
          </div>
        </div>
        <IconButton label="Dismiss" size="small" onClick={hide}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </IconButton>
      </div>
    </div>
  );
}

export default PasskeyPromptBanner;
